import {
  type CSSProperties,
  type ForwardedRef,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import useAccessibleDropdown from "$/lib/hooks/useAccessibleDropdown";
import { cn } from "$/lib/utils/functions/misc.functions";

import ComboboxInputOptionsList from "./ComboboxInputOptionsList";

import ChevronUpIcon from "$/lib/icons/ChevronUpIcon";
import SearchIcon from "$/lib/icons/SearchIcon";
import type { ComboboxProps } from "./ComboboxInput.types";

const PADDING = 10;

function ComboboxInputInner<T>(
  {
    label,
    hideLabel,
    placeholder,
    options,
    isSearching,
    selected,
    multi,
    isError,
    disabled,
    containerClassName,
    buttonClassName,
    overlayClassName,
    onSelect,
    onSearch,
  }: ComboboxProps<T>,
  forwardedTriggerRef: ForwardedRef<HTMLButtonElement>,
) {
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const [selectionLabel, setSelectionLabel] = useState("");

  const listId = useId();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const optionsListRef = useRef<HTMLUListElement | null>(null);

  const { isExpanded, overlayProps, overlayPlacement, close, toggleExpanded } =
    useAccessibleDropdown({
      containerRef,
      triggerRef,
      overlayRef,
    });

  const overlayStyle = overlayProps.style as CSSProperties;

  const handleScrollToOption = useCallback((focusedIndex: number) => {
    if (focusedIndex === -1) return;

    const list = optionsListRef.current;
    if (!list) return;

    const focusedOption = list.childNodes[focusedIndex] as
      | HTMLElement
      | undefined;

    if (!focusedOption) return;

    const listRect = list.getBoundingClientRect();
    const optionRect = focusedOption.getBoundingClientRect();

    if (optionRect.bottom > listRect.bottom) {
      list.scrollTo({
        top: list.scrollTop + optionRect.bottom - listRect.bottom + PADDING,
      });
    }

    if (optionRect.top < listRect.top) {
      list.scrollTo({
        top: list.scrollTop - (listRect.top - optionRect.top) - PADDING,
      });
    }
  }, []);

  const handleSelect = useCallback(
    (option: T, index: number) => {
      onSelect(option, index);
      if (!multi) {
        close();
        optionsListRef.current?.scrollTo({ top: 0 });
      }
    },
    [multi, onSelect, close],
  );

  useEffect(() => {
    if (!multi) {
      const selectedOption = options.find(
        (option) => option.value === selected,
      );
      if (selectedOption) {
        setSelectionLabel(selectedOption.label);
      }
    }
  }, [multi, selected, options]);

  useEffect(() => {
    if (isExpanded) {
      searchInputRef.current?.focus({
        preventScroll: true,
      });
      return;
    }

    const searchInput = searchInputRef.current;
    if (searchInput) {
      searchInput.value = "";
      onSearch?.("");
    }

    optionsListRef.current?.scrollTo({ top: 0 });

    setFocusedOptionIndex(-1);
  }, [isExpanded, onSearch]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleArrowsMovement = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        setFocusedOptionIndex((p) => {
          if (p === -1) return 0;
          const newFocusedOptionIndex = Math.min(p + 1, options.length - 1);
          handleScrollToOption(newFocusedOptionIndex);
          return newFocusedOptionIndex;
        });
      }

      if (e.key === "ArrowUp") {
        setFocusedOptionIndex((p) => {
          const newFocusedOptionIndex = Math.max(p - 1, 0);
          handleScrollToOption(newFocusedOptionIndex);
          return newFocusedOptionIndex;
        });
      }
    };

    const handleBlockInputArrowsMovement = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") e.preventDefault();
    };

    const overlay = overlayRef.current;
    const searchInput = searchInputRef.current;

    overlay?.addEventListener("keydown", handleArrowsMovement);
    searchInput?.addEventListener("keydown", handleBlockInputArrowsMovement);

    return () => {
      overlay?.removeEventListener("keydown", handleArrowsMovement);
      searchInput?.removeEventListener(
        "keydown",
        handleBlockInputArrowsMovement,
      );
    };
  }, [isExpanded, options.length, handleScrollToOption]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyboardSelection = (e: KeyboardEvent) => {
      if (e.key === "Enter" && focusedOptionIndex !== -1) {
        e.preventDefault();
        e.stopPropagation();
        const focusedOption = options.at(focusedOptionIndex);
        if (focusedOption) {
          handleSelect(focusedOption.value, focusedOptionIndex);
          if (!multi) close();
        }
      }
    };

    const overlay = overlayRef.current;

    overlay?.addEventListener("keydown", handleKeyboardSelection);

    return () => {
      overlay?.removeEventListener("keydown", handleKeyboardSelection);
    };
  }, [focusedOptionIndex, isExpanded, multi, options, handleSelect, close]);

  const placeholderText = multi ? placeholder : selectionLabel || placeholder;

  const setTriggerRef = (el: HTMLButtonElement) => {
    triggerRef.current = el;
    if (forwardedTriggerRef) {
      if (typeof forwardedTriggerRef === "function") {
        forwardedTriggerRef(el);
      } else {
        forwardedTriggerRef.current = el;
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("group relative w-full", containerClassName)}
    >
      {!!label && !hideLabel && (
        <label className="mb-1 ml-2 block text-xs text-black">{label}</label>
      )}
      <button
        ref={setTriggerRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-autocomplete="none"
        aria-expanded={isExpanded}
        aria-haspopup="listbox"
        aria-invalid={isError}
        aria-controls={listId}
        onClick={toggleExpanded}
        className={cn(
          "data-[placeholder]:text-grey-200 inline-flex h-12 min-h-12 w-full items-center justify-between gap-4 rounded-lg border-2 bg-tertiary-200 px-4 py-2.5 text-sm font-medium leading-none text-black shadow-sm outline-none duration-150 group-focus-within:border-primary/30 data-[state='open']:border-primary/30",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "aria-invalid:border-error aria-invalid:group-focus-within:border-primary/30",
          buttonClassName,
        )}
      >
        <span
          className="inline-flex w-full items-center gap-2"
          title={placeholderText}
        >
          <span className="line-clamp-1 text-ellipsis text-left text-black">
            {placeholderText}
          </span>
          {multi && (
            <span
              className={cn(
                "text-snow size-5 rounded-circle bg-primary p-0.5 text-xs duration-100",
                !selected?.length && "opacity-0",
              )}
            >
              {selected?.length || 1}
            </span>
          )}
        </span>
        <ChevronUpIcon fill="black" className="size-2.5 rotate-180" />
      </button>
      <div
        tabIndex={-1}
        aria-expanded={isExpanded}
        data-placement={overlayPlacement}
        className={cn(
          "pointer-events-none absolute z-30 w-full min-w-40 -translate-y-2 overflow-hidden rounded-md bg-white px-2 py-4 pb-0 opacity-0 shadow-2xl duration-150 aria-expanded:pointer-events-auto data-[placement='top']:translate-y-2 data-[placement='top']:aria-expanded:translate-y-0",
          "aria-expanded:translate-y-0 aria-expanded:opacity-100",
          overlayClassName,
        )}
        ref={overlayRef}
        {...overlayProps}
        style={overlayStyle}
      >
        <div className="relative mb-4">
          <input
            ref={searchInputRef}
            placeholder="Chercher"
            className="h-9 w-full rounded-pill border-0 border-b border-b-tertiary-300 pl-8 text-black outline-none placeholder:text-tertiary-400 focus:border-b-primary/30"
            onChange={(e) => onSearch?.(e.target.value)}
          />

          <SearchIcon className="absolute left-1.5 top-1/2 size-6 -translate-y-1/2" />
        </div>

        {isSearching ? (
          <ul className="h-56 space-y-1 overflow-scroll pb-1">
            {Array.from({ length: 6 }).map((_, i) => {
              return (
                <li
                  key={i}
                  role="option"
                  className="h-10 w-full animate-pulse rounded-md bg-tertiary-200"
                />
              );
            })}
          </ul>
        ) : (
          <ComboboxInputOptionsList
            id={listId}
            ref={optionsListRef}
            options={options}
            focusedOptionIndex={focusedOptionIndex}
            handleSetFocusedOptionIndex={(i) => setFocusedOptionIndex(i)}
            handleSelect={handleSelect}
            multi={multi ?? false}
            selected={selected}
          />
        )}
      </div>
    </div>
  );
}

const ComboboxInput = forwardRef(ComboboxInputInner) as {
  <T>(
    props: ComboboxProps<T> & { ref?: ForwardedRef<HTMLButtonElement> },
  ): ReactNode;
  displayName: string;
};
ComboboxInput.displayName = "ComboboxInput";

export default ComboboxInput;
